import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { BUILDINGS } from '../../lib/workerData';

const FREQ_OPTIONS = [
  'daily', 'twice-daily', 'nightly', 'weekly', 'monthly',
  'semi-annual', 'annual', 'after-rental',
];

// Buildings this manager-editable system currently covers. Every other
// building still runs on the static file in lib/checklists.js until it
// gets migrated the same way E and F were.
function usableBuildings() {
  return BUILDINGS.filter(b => b.status !== 'inactive');
}

export default function TaskEditor() {
  const buildings = usableBuildings();
  const [buildingId, setBuildingId] = useState('E');
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newZoneName, setNewZoneName] = useState('');
  const [newTaskDrafts, setNewTaskDrafts] = useState({}); // zoneId -> { label, freq }

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('checklist_zones')
      .select('id, name, note, sort_order, checklist_tasks(id, label, freq, sort_order)')
      .eq('building_id', buildingId)
      .order('sort_order', { ascending: true });
    if (err) {
      setError(err.message);
      setLoading(false);
      return;
    }
    setZones((data || []).map(z => ({
      ...z,
      checklist_tasks: [...z.checklist_tasks].sort((a, b) => a.sort_order - b.sort_order),
    })));
    setLoading(false);
  }, [buildingId]);

  useEffect(() => { load(); }, [load]);

  async function addZone() {
    if (!newZoneName.trim()) return;
    const nextSort = zones.length ? Math.max(...zones.map(z => z.sort_order)) + 1 : 0;
    const { error: err } = await supabase.from('checklist_zones').insert([{
      building_id: buildingId, name: newZoneName.trim(), sort_order: nextSort,
    }]);
    if (err) { setError(err.message); return; }
    setNewZoneName('');
    await load();
  }

  async function updateZone(zoneId, patch) {
    const { error: err } = await supabase.from('checklist_zones').update(patch).eq('id', zoneId);
    if (err) { setError(err.message); return; }
  }

  async function deleteZone(zone) {
    if (!window.confirm(`Remove the "${zone.name}" section and all ${zone.checklist_tasks.length} of its tasks?`)) return;
    const { error: err } = await supabase.from('checklist_zones').delete().eq('id', zone.id);
    if (err) { setError(err.message); return; }
    await load();
  }

  async function addTask(zone) {
    const draft = newTaskDrafts[zone.id];
    if (!draft?.label?.trim()) return;
    const nextSort = zone.checklist_tasks.length
      ? Math.max(...zone.checklist_tasks.map(t => t.sort_order)) + 1 : 0;
    const { error: err } = await supabase.from('checklist_tasks').insert([{
      zone_id: zone.id, label: draft.label.trim(), freq: draft.freq || 'daily', sort_order: nextSort,
    }]);
    if (err) { setError(err.message); return; }
    setNewTaskDrafts(prev => ({ ...prev, [zone.id]: { label: '', freq: draft.freq || 'daily' } }));
    await load();
  }

  async function updateTask(taskId, patch) {
    const { error: err } = await supabase.from('checklist_tasks').update(patch).eq('id', taskId);
    if (err) { setError(err.message); return; }
  }

  async function deleteTask(task) {
    const { error: err } = await supabase.from('checklist_tasks').delete().eq('id', task.id);
    if (err) { setError(err.message); return; }
    await load();
  }

  const migrated = zones.length > 0;

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <div style={s.title}>Building Tasks</div>
          <div style={s.subtitle}>What workers see and check off when they check in. Changes apply immediately.</div>
        </div>
      </div>

      <div style={s.buildingRow}>
        {buildings.map(b => (
          <button key={b.id}
            style={buildingId === b.id ? { ...s.buildingBtn, ...s.buildingBtnActive } : s.buildingBtn}
            onClick={() => setBuildingId(b.id)}>
            {b.id} — {b.shortName || b.name}
          </button>
        ))}
      </div>

      {error && (
        <div style={s.errorBanner}>{error} <button style={s.errorDismiss} onClick={() => setError(null)}>✕</button></div>
      )}

      {loading ? (
        <div style={s.loading}>Loading tasks…</div>
      ) : !migrated ? (
        <div style={s.empty}>
          No tasks set up yet for this building — it's still running on the built-in default list.
          Add a section below to start managing it here instead.
          <div style={s.addZoneRow}>
            <input style={s.input} placeholder="Section name, e.g. Washrooms" value={newZoneName}
              onChange={e => setNewZoneName(e.target.value)} />
            <button style={s.addBtn} onClick={addZone}>+ Add Section</button>
          </div>
        </div>
      ) : (
        <>
          <div style={s.zoneList}>
            {zones.map(zone => (
              <div key={zone.id} style={s.zoneCard}>
                <div style={s.zoneHeader}>
                  <input style={s.zoneNameInput} defaultValue={zone.name}
                    onBlur={e => e.target.value.trim() && e.target.value !== zone.name && updateZone(zone.id, { name: e.target.value.trim() })} />
                  <button style={s.zoneDeleteBtn} onClick={() => deleteZone(zone)}>Remove Section</button>
                </div>
                <input style={s.zoneNoteInput} placeholder="Note for this section (optional)" defaultValue={zone.note || ''}
                  onBlur={e => e.target.value !== (zone.note || '') && updateZone(zone.id, { note: e.target.value || null })} />

                <div style={s.taskTable}>
                  <div style={s.taskTableHead}>
                    <span>Task</span><span>Frequency</span><span /></div>
                  {zone.checklist_tasks.map(task => (
                    <div key={task.id} style={s.taskRow}>
                      <input style={s.taskLabelInput} defaultValue={task.label}
                        onBlur={e => e.target.value.trim() && e.target.value !== task.label && updateTask(task.id, { label: e.target.value.trim() })} />
                      <select style={s.taskFreqSelect} defaultValue={task.freq}
                        onChange={e => updateTask(task.id, { freq: e.target.value })}>
                        {FREQ_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                      <button style={s.taskDeleteBtn} onClick={() => deleteTask(task)}>✕</button>
                    </div>
                  ))}
                  <div style={s.taskRow}>
                    <input style={s.taskLabelInput} placeholder="New task…"
                      value={newTaskDrafts[zone.id]?.label || ''}
                      onChange={e => setNewTaskDrafts(prev => ({ ...prev, [zone.id]: { ...prev[zone.id], label: e.target.value } }))}
                      onKeyDown={e => { if (e.key === 'Enter') addTask(zone); }} />
                    <select style={s.taskFreqSelect}
                      value={newTaskDrafts[zone.id]?.freq || 'daily'}
                      onChange={e => setNewTaskDrafts(prev => ({ ...prev, [zone.id]: { ...prev[zone.id], freq: e.target.value } }))}>
                      {FREQ_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                    <button style={s.taskAddBtn} onClick={() => addTask(zone)}>+ Add</button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={s.addZoneRow}>
            <input style={s.input} placeholder="New section name, e.g. Storage Room" value={newZoneName}
              onChange={e => setNewZoneName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addZone(); }} />
            <button style={s.addBtn} onClick={addZone}>+ Add Section</button>
          </div>
        </>
      )}
    </div>
  );
}

const s = {
  page: { padding: 20, background: '#F5F3EF', minHeight: 'calc(100vh - 130px)' },
  header: { marginBottom: 16 },
  title: { fontSize: 24, fontWeight: 800, color: '#1A2B33', letterSpacing: -0.5 },
  subtitle: { fontSize: 13, color: '#5B6B72', marginTop: 4 },
  buildingRow: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 },
  buildingBtn: { background: '#FFFFFF', border: '1px solid #E4E0D8', color: '#5B6B72', borderRadius: 20, padding: '6px 14px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' },
  buildingBtnActive: { background: '#f97316', borderColor: '#f97316', color: '#FFFFFF' },
  errorBanner: { background: '#FBF1EF', border: '1px solid #B33A2E', color: '#B33A2E', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  errorDismiss: { background: 'none', border: 'none', color: '#B33A2E', cursor: 'pointer', fontSize: 13 },
  loading: { padding: 40, textAlign: 'center', color: '#5B6B72' },
  empty: { background: '#FFFFFF', border: '1px dashed #E4E0D8', borderRadius: 10, padding: 24, color: '#5B6B72', fontSize: 14, lineHeight: 1.6 },
  zoneList: { display: 'flex', flexDirection: 'column', gap: 14 },
  zoneCard: { background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 12, padding: 16 },
  zoneHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 6 },
  zoneNameInput: { flex: 1, fontSize: 16, fontWeight: 700, color: '#1A2B33', border: '1px solid transparent', borderRadius: 6, padding: '4px 6px', background: 'transparent', outline: 'none' },
  zoneDeleteBtn: { background: 'transparent', border: '1px solid #E4E0D8', color: '#B33A2E', borderRadius: 6, padding: '5px 10px', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' },
  zoneNoteInput: { width: '100%', fontSize: 12.5, color: '#5B6B72', border: '1px solid transparent', borderRadius: 6, padding: '4px 6px', background: 'transparent', outline: 'none', boxSizing: 'border-box', marginBottom: 10, fontStyle: 'italic' },
  taskTable: { display: 'flex', flexDirection: 'column', gap: 4 },
  taskTableHead: { display: 'grid', gridTemplateColumns: '1fr 140px 32px', gap: 8, fontSize: 10.5, fontWeight: 700, color: '#5B6B72', textTransform: 'uppercase', letterSpacing: 0.5, padding: '0 4px 4px' },
  taskRow: { display: 'grid', gridTemplateColumns: '1fr 140px 32px', gap: 8, alignItems: 'center' },
  taskLabelInput: { width: '100%', minWidth: 0, fontSize: 13.5, color: '#1A2B33', border: '1px solid #E4E0D8', borderRadius: 6, padding: '7px 9px', outline: 'none', boxSizing: 'border-box' },
  taskFreqSelect: { width: '100%', minWidth: 0, fontSize: 12.5, color: '#1A2B33', border: '1px solid #E4E0D8', borderRadius: 6, padding: '6px 8px', outline: 'none', background: '#FFFFFF', boxSizing: 'border-box' },
  taskDeleteBtn: { background: 'transparent', border: 'none', color: '#5B6B72', fontSize: 14, cursor: 'pointer' },
  taskAddBtn: { background: '#FFFFFF', border: '1px solid #2F7A4F', color: '#2F7A4F', borderRadius: 6, padding: '6px 4px', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  addZoneRow: { display: 'flex', gap: 10, marginTop: 16 },
  input: { flex: 1, background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 8, padding: '10px 12px', color: '#1A2B33', fontSize: 14, outline: 'none' },
  addBtn: { background: '#f97316', color: '#FFFFFF', border: 'none', borderRadius: 8, padding: '10px 18px', fontWeight: 700, fontSize: 13.5, cursor: 'pointer', whiteSpace: 'nowrap' },
};
